/**
 * Connected Persons API functions
 * For managing non-family members who are connected to tree members
 */

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8000/api";

// Types
export interface ConnectedPersonSummary {
  id: number;
  gedcom_id: string | null;
  given_name: string | null;
  surname: string | null;
  full_name: string | null;
  sex: string | null;
  birth_date_text: string | null;
  death_date_text: string | null;
}

export interface PersonLinkInfo {
  id: number;
  person_id: number;
  person_name: string;
  tree_id: number;
  tree_name: string;
  relationship_type: string | null;
  relationship_description: string | null;
  created_at: string;
}

export interface ConnectedPersonDetail extends ConnectedPersonSummary {
  birth_date: string | null;
  birth_place_text: string | null;
  death_date: string | null;
  death_place_text: string | null;
  notes: string | null;
  person_links: PersonLinkInfo[];
  created_at: string;
  updated_at: string;
}

export interface ConnectedPersonCreate {
  given_name?: string;
  surname?: string;
  full_name?: string;
  sex?: string;
  gedcom_id?: string;
  birth_date?: string;
  birth_date_text?: string;
  birth_place_text?: string;
  death_date?: string;
  death_date_text?: string;
  death_place_text?: string;
  notes?: string;
}

export interface ConnectedPersonLinkCreate {
  person_id: number;
  relationship_type?: string;
  relationship_description?: string;
}

export interface ConnectedToPersonInfo {
  id: number;
  connected_person_id: number;
  connected_person_name: string;
  relationship_type: string | null;
  relationship_description: string | null;
}

// Relationship type options
export const RELATIONSHIP_TYPES = [
  { value: "friend", label: "Friend" },
  { value: "neighbor", label: "Neighbor" },
  { value: "colleague", label: "Colleague" },
  { value: "employer", label: "Employer" },
  { value: "employee", label: "Employee" },
  { value: "teacher", label: "Teacher" },
  { value: "student", label: "Student" },
  { value: "business_partner", label: "Business Partner" },
  { value: "landlord", label: "Landlord" },
  { value: "tenant", label: "Tenant" },
  { value: "godparent", label: "Godparent" },
  { value: "godchild", label: "Godchild" },
  { value: "other", label: "Other" },
];

// =============================================================================
// CONNECTED PERSONS CRUD
// =============================================================================

/**
 * List all connected persons
 */
export async function listConnectedPersons(
  token: string,
  search?: string,
  limit?: number,
  offset?: number
): Promise<ConnectedPersonSummary[]> {
  const params = new URLSearchParams();
  if (search) params.set("search", search);
  if (limit) params.set("limit", String(limit));
  if (offset) params.set("offset", String(offset));

  const response = await fetch(`${API_URL}/connected-persons?${params.toString()}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch connected persons");
  }

  return response.json();
}

/**
 * Get connected person details
 */
export async function getConnectedPerson(
  id: number,
  token: string
): Promise<ConnectedPersonDetail> {
  const response = await fetch(`${API_URL}/connected-persons/${id}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch connected person");
  }

  return response.json();
}

/**
 * Create a new connected person
 */
export async function createConnectedPerson(
  data: ConnectedPersonCreate,
  token: string
): Promise<ConnectedPersonSummary> {
  const response = await fetch(`${API_URL}/connected-persons`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: "Failed to create connected person" }));
    throw new Error(error.detail || "Failed to create connected person");
  }

  return response.json();
}

/**
 * Update a connected person
 */
export async function updateConnectedPerson(
  id: number,
  data: ConnectedPersonCreate,
  token: string
): Promise<ConnectedPersonSummary> {
  const response = await fetch(`${API_URL}/connected-persons/${id}`, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: "Failed to update connected person" }));
    throw new Error(error.detail || "Failed to update connected person");
  }

  return response.json();
}

/**
 * Delete a connected person
 */
export async function deleteConnectedPerson(
  id: number,
  token: string
): Promise<void> {
  const response = await fetch(`${API_URL}/connected-persons/${id}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error("Failed to delete connected person");
  }
}

// =============================================================================
// PERSON LINKS
// =============================================================================

/**
 * Create a link between connected person and tree person
 */
export async function createPersonLink(
  connectedPersonId: number,
  data: ConnectedPersonLinkCreate,
  token: string
): Promise<PersonLinkInfo> {
  const response = await fetch(`${API_URL}/connected-persons/${connectedPersonId}/links`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: "Failed to create link" }));
    throw new Error(error.detail || "Failed to create link");
  }

  return response.json();
}

/**
 * Update a link
 */
export async function updatePersonLink(
  connectedPersonId: number,
  linkId: number,
  data: Partial<ConnectedPersonLinkCreate>,
  token: string
): Promise<PersonLinkInfo> {
  const response = await fetch(`${API_URL}/connected-persons/${connectedPersonId}/links/${linkId}`, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error("Failed to update link");
  }

  return response.json();
}

/**
 * Delete a link
 */
export async function deletePersonLink(
  connectedPersonId: number,
  linkId: number,
  token: string
): Promise<void> {
  const response = await fetch(`${API_URL}/connected-persons/${connectedPersonId}/links/${linkId}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error("Failed to delete link");
  }
}

/**
 * Get connected persons for a tree person
 */
export async function getConnectedPersonsForPerson(
  personId: number,
  token: string
): Promise<ConnectedToPersonInfo[]> {
  const response = await fetch(`${API_URL}/connected-persons/by-person/${personId}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch connected persons");
  }

  return response.json();
}

// =============================================================================
// UTILITIES
// =============================================================================

/**
 * Get display name for a connected person
 */
export function getConnectedPersonDisplayName(person: ConnectedPersonSummary): string {
  if (person.full_name) return person.full_name;
  const parts = [];
  if (person.given_name) parts.push(person.given_name);
  if (person.surname) parts.push(person.surname);
  return parts.join(" ") || "Unknown";
}

/**
 * Get relationship type label
 */
export function getRelationshipTypeLabel(type: string | null): string {
  if (!type) return "Unknown";
  const found = RELATIONSHIP_TYPES.find(t => t.value === type);
  return found?.label || type;
}
